//reviewFrameSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { logout } from "../actions/authenActions";

const baseURL = process.env.REACT_APP_API_URL || "http://localhost:8000";

export const STATUS_NOT_APPROVED = "Not Approved";
export const STATUS_APPROVED = "Approved";
export const STATUS_APPROVED_CLOSED = "Approved and Closed";
export const STATUS_APPROVED_INPROCESS = "Approved and In Process";

const computeStatus = (approved, processed, inprocess) => {
  if (approved !== true) return STATUS_NOT_APPROVED;
  if (processed === true) return STATUS_APPROVED_CLOSED;
  return inprocess === true ? STATUS_APPROVED_INPROCESS : STATUS_APPROVED;
};

const statusToTotalKey = (status) => {
  if (status === STATUS_APPROVED_INPROCESS) return "approved_inprocess";
  if (status === STATUS_APPROVED_CLOSED) return "approved_closed";
  if (status === STATUS_APPROVED) return "approved";
  return "not_approved";
};

// Moves one report from oldStatus to newStatus in the totals tiles
const adjustTotals = (totals, oldStatus, newStatus) => {
  if (oldStatus === newStatus) return;
  const oldKey = statusToTotalKey(oldStatus);
  const newKey = statusToTotalKey(newStatus);
  totals[oldKey] = Math.max(0, (totals[oldKey] || 0) - 1);
  totals[newKey] = (totals[newKey] || 0) + 1;
};

// Fetch ALL records + status totals for the license
export const fetchReviewRecords = createAsyncThunk(
  "reviewFrame/fetchReviewRecords",
  async ({ liverun, license_key }, { dispatch, rejectWithValue }) => {
    try {
      const res = await axios.post(`${baseURL}/emis_pathology/api/ready_data`, {
        liverun,
        license_key,
      });
      return res.data || {};
    } catch (error) {
      if (error.response && error.response.status === 403) {
        dispatch(logout());
      }
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  },
);

export const fetchLabReport = createAsyncThunk(
  "reviewFrame/fetchLabReport",
  async (
    { lab_report_id, liverun, license_key },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const res = await axios.post(
        `${baseURL}/emis_pathology/api/ready_count_lab_report`,
        { lab_report_id, liverun, license_key },
      );
      return res.data || [];
    } catch (error) {
      if (error.response && error.response.status === 403) {
        dispatch(logout());
      }
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  },
);

export const saveLabReportActions = createAsyncThunk(
  "reviewFrame/saveLabReportActions",
  async (
    { lab_report_id, actions, liverun, license_key },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const res = await axios.post(
        `${baseURL}/emis_pathology/api/save_lab_report_actions`,
        { lab_report_id, actions, liverun, license_key },
      );
      return res.data;
    } catch (error) {
      if (error.response && error.response.status === 403) {
        dispatch(logout());
      }
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  },
);

export const loadLabReportActions = createAsyncThunk(
  "reviewFrame/loadLabReportActions",
  async (
    { lab_report_id, liverun, license_key },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const res = await axios.post(
        `${baseURL}/emis_pathology/api/load_lab_report_actions`,
        { lab_report_id, liverun, license_key },
      );
      return res.data.actions || [];
    } catch (error) {
      if (error.response && error.response.status === 403) {
        dispatch(logout());
      }
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  },
);

// Approve lab report (Not Approved -> Approved)
export const approveLabReport = createAsyncThunk(
  "reviewFrame/approveLabReport",
  async (
    { lab_report_id, liverun, license_key },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const res = await axios.post(
        `${baseURL}/emis_pathology/api/approve_lab_report`,
        { lab_report_id, liverun, license_key },
      );
      return { lab_report_id, categories: res.data.categories || [] };
    } catch (error) {
      if (error.response && error.response.status === 403) {
        dispatch(logout());
      }
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  },
);

// Unapprove lab report (Approved -> Not Approved)
export const unapproveLabReport = createAsyncThunk(
  "reviewFrame/unapproveLabReport",
  async (
    { lab_report_id, liverun, license_key },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const res = await axios.post(
        `${baseURL}/emis_pathology/api/unapprove_lab_report`,
        { lab_report_id, liverun, license_key },
      );
      return { lab_report_id, categories: res.data.categories || [] };
    } catch (error) {
      if (error.response && error.response.status === 403) {
        dispatch(logout());
      }
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  },
);

// === SLICE ===

const initialState = {
  liverun: true,
  records: [],
  totals: {
    not_approved: 0,
    approved: 0,
    approved_closed: 0,
    approved_inprocess: 0,
  },
  lab: {},
  selectedLabReport: null,
  loadingRecords: false,
  loadingLabReport: false,
  labReportActions: [],
  loadingActions: false,
  savingActions: false,
  updatingApproval: false,
};

const reviewFrameSlice = createSlice({
  name: "reviewFrame",
  initialState,
  reducers: {
    toggleLiverun(state) {
      state.liverun = !state.liverun;

      // Reset related state
      state.records = [];
      state.totals = {
        not_approved: 0,
        approved: 0,
        approved_closed: 0,
        approved_inprocess: 0,
      };
      state.lab = {};
      state.selectedLabReport = null;
      state.labReportActions = [];

      // Reset loading and action flags
      state.loadingRecords = false;
      state.loadingLabReport = false;
      state.loadingActions = false;
      state.savingActions = false;
      state.updatingApproval = false;
    },
    setLabReport(state, action) {
      state.selectedLabReport = action.payload;
    },
    setLabReportActions(state, action) {
      state.labReportActions = action.payload;
    },
    clearLabReportActions(state) {
      state.labReportActions = [];
    },
    clearLabReport(state) {
      state.lab = {};
      state.labReportActions = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Records + totals
      .addCase(fetchReviewRecords.pending, (state) => {
        state.loadingRecords = true;
      })
      .addCase(fetchReviewRecords.fulfilled, (state, action) => {
        const { records = [], totals = {} } = action.payload;

        state.records = records.map((rec, idx) => ({
          id: idx + 1,
          nhs_num: rec.nhs_num ?? "",
          emis_id: rec.emis_id ?? "",
          lab_report_id: rec.lab_report_id ?? "",
          ordering_party: rec.ordering_party ?? "",
          usual_gp: rec.usual_gp ?? "",
          category: rec.category || "",
          categories: Array.isArray(rec.categories) ? rec.categories : [],
          is_normal: typeof rec.is_normal === "boolean" ? rec.is_normal : null,
          approved: rec.approved === true,
          processed: rec.processed === true,
          inprocess: rec.inprocess === true,
          status:
            rec.status ||
            computeStatus(rec.approved, rec.processed, rec.inprocess),
          processed_time: rec.processed_time || "",
          notes: rec.notes || "",
        }));

        state.totals = {
          not_approved: totals.not_approved || 0,
          approved: totals.approved || 0,
          approved_closed: totals.approved_closed || 0,
          approved_inprocess: totals.approved_inprocess || 0,
        };
        state.loadingRecords = false;
      })
      .addCase(fetchReviewRecords.rejected, (state) => {
        state.loadingRecords = false;
      })

      // Lab Report
      .addCase(fetchLabReport.pending, (state) => {
        state.loadingLabReport = true;
      })
      .addCase(fetchLabReport.fulfilled, (state, action) => {
        const report = action.payload;

        state.lab = {
          license_email: report.license_email || "",
          approved: report.approved ?? null,
          updated_at: report.updated_at || "",
          updated_by: report.updated_by || "",
          patient_data: report.patient_data || {},
          patient_profile: report.patient_profile || {},
          categories: Array.isArray(report.categories) ? report.categories : [],
          actions: Array.isArray(report.actions) ? report.actions : [],
          filing_error: report.filing_error || "",
        };

        state.labReportActions = report.actions || [];
        state.loadingLabReport = false;
      })
      .addCase(fetchLabReport.rejected, (state) => {
        state.loadingLabReport = false;
      })

      // Save Lab Report Actions
      .addCase(saveLabReportActions.pending, (state) => {
        state.savingActions = true;
      })
      .addCase(saveLabReportActions.fulfilled, (state, action) => {
        state.savingActions = false;
        if (action.payload.actions) {
          state.labReportActions = action.payload.actions;
        }
      })
      .addCase(saveLabReportActions.rejected, (state) => {
        state.savingActions = false;
      })

      // Approve Lab Report
      .addCase(approveLabReport.pending, (state) => {
        state.updatingApproval = true;
      })
      .addCase(approveLabReport.fulfilled, (state, action) => {
        const { lab_report_id } = action.payload;

        state.lab.approved = true;

        const row = state.records.find(
          (r) => r.lab_report_id === lab_report_id,
        );
        if (row) {
          const oldStatus = row.status;
          row.approved = true;
          row.status = computeStatus(true, row.processed, row.inprocess);
          adjustTotals(state.totals, oldStatus, row.status);
        }

        state.updatingApproval = false;
      })
      .addCase(approveLabReport.rejected, (state) => {
        state.updatingApproval = false;
      })

      // Unapprove Lab Report
      .addCase(unapproveLabReport.pending, (state) => {
        state.updatingApproval = true;
      })
      .addCase(unapproveLabReport.fulfilled, (state, action) => {
        const { lab_report_id } = action.payload;

        state.lab.approved = false;

        const row = state.records.find(
          (r) => r.lab_report_id === lab_report_id,
        );
        if (row) {
          const oldStatus = row.status;
          row.approved = false;
          row.status = computeStatus(false, row.processed, row.inprocess);
          adjustTotals(state.totals, oldStatus, row.status);
        }

        state.updatingApproval = false;
      })
      .addCase(unapproveLabReport.rejected, (state) => {
        state.updatingApproval = false;
      });
  },
});

export const {
  toggleLiverun,
  setLabReport,
  setLabReportActions,
  clearLabReportActions,
  clearLabReport,
} = reviewFrameSlice.actions;

export default reviewFrameSlice.reducer;
